/**
 * Billing state from Stripe's webhooks, as a change to an organisation's row
 * (Stage 5). Pure: the worker checks the signature, hands the event here, and
 * writes what comes back.
 *
 * - **Which organisation:** the subscription's `metadata.organization_id`,
 *   set when the subscription is made. An event without one is not utuwrk's
 *   to act on and changes nothing.
 * - **Which plan:** the price's `lookup_key`, which is one of the plan ids
 *   (`starter`, `growth`, `business`). Naming plans in Stripe rather than
 *   mapping price ids here means a new price is a Stripe change, not a deploy.
 * - **Failing open:** a subscription past due, unpaid, incomplete or paused
 *   keeps the plan it had. Only a subscription that has ended moves the
 *   organisation to Free, which keeps its data (PRD 13.2's permanent free
 *   tier); closing an organisation is a separate act with its own window.
 *
 * Events can arrive out of order and more than once. Each change carries the
 * event's own time, and the worker writes it only over an older one; it also
 * keeps every event id it has applied, so a replay changes nothing.
 */

import { isPlanId, type PlanId } from "./plans.js";

export interface BillingChange {
  eventId: string;
  type: string;
  orgId: string;
  /** The plan to move to, or null to keep the one it has. */
  plan: PlanId | null;
  /** Stripe's subscription status, as it said it. */
  status: string;
  customer: string | null;
  subscription: string | null;
  /** When Stripe made the event, in seconds: a change older than the one stored is not written. */
  at: number;
}

const SUBSCRIPTION_EVENTS = [
  "customer.subscription.created",
  "customer.subscription.updated",
  "customer.subscription.deleted",
  "customer.subscription.paused",
  "customer.subscription.resumed",
];

/** Statuses in which the customer is paying, or about to: the plan is the price's. */
const PAYING = ["active", "trialing"];
/** Statuses in which the subscription is over: back to Free. */
const ENDED = ["canceled", "incomplete_expired"];

const text = (value: unknown): string | null => (typeof value === "string" && value ? value : null);
const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

/** The plan a subscription's first item is priced as, by its lookup key, or null when it names none. */
function pricedPlan(subscription: Record<string, unknown>): PlanId | null {
  const items = record(subscription.items).data;
  if (!Array.isArray(items)) return null;
  for (const item of items) {
    const key = record(record(item).price).lookup_key;
    if (isPlanId(key)) return key;
  }
  return null;
}

/** What a webhook event changes, or null when it is not a subscription event about one of utuwrk's organisations. */
export function billingChange(event: unknown): BillingChange | null {
  const raw = record(event);
  const type = text(raw.type);
  const eventId = text(raw.id);
  const at = typeof raw.created === "number" ? raw.created : null;
  if (!type || !eventId || at === null || !SUBSCRIPTION_EVENTS.includes(type)) return null;
  const subscription = record(record(raw.data).object);
  const orgId = text(record(subscription.metadata).organization_id);
  if (!orgId) return null;
  const status = text(subscription.status) ?? (type === "customer.subscription.deleted" ? "canceled" : "");

  let plan: PlanId | null = null;
  if (type === "customer.subscription.deleted" || ENDED.includes(status)) plan = "free";
  else if (PAYING.includes(status)) plan = pricedPlan(subscription);

  return {
    eventId,
    type,
    orgId,
    plan,
    status,
    customer: text(subscription.customer),
    subscription: text(subscription.id),
    at,
  };
}

/**
 * Whether an organisation still has a subscription in Stripe: paying, late,
 * paused or not yet finished. Such an organisation changes plan or cancels in
 * Stripe's customer portal; a second checkout would make a second subscription.
 */
export const hasSubscription = (status: string | null): boolean =>
  status !== null && status !== "" && !ENDED.includes(status);

/** The plans a checkout sells: every plan but Free, which needs no card. */
export const PAID_PLANS = ["starter", "growth", "business"] as const satisfies readonly PlanId[];
export type PaidPlanId = (typeof PAID_PLANS)[number];

export const isPaidPlan = (value: unknown): value is PaidPlanId => PAID_PLANS.includes(value as PaidPlanId);

/** Why a checkout for `plan` cannot start, or null when it can. */
export function checkoutProblem(plan: unknown, status: string | null): string | null {
  if (!isPaidPlan(plan)) return "Choose Starter, Growth or Business.";
  if (hasSubscription(status))
    return "This organisation already pays through Stripe. Change its plan from Manage billing.";
  return null;
}

/** How the console's record says it: "growth, active (sub_123)". */
export function billingNote(change: BillingChange): string {
  return `${change.plan ?? "plan kept"}, ${change.status || "no status"}${change.subscription ? ` (${change.subscription})` : ""}, from Stripe ${change.type}`;
}
